import { ArrowLeftIcon, InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import useIsActive from "hooks/useIsActive";

import { ROUTES } from "utils/constants";

import Button from "components/Button";
import { ButtonGroup } from "components/ButtonGroup";
import Container from "components/Container";
import Content from "components/Content";
import Footer from "components/Footer";
import { Form, FormControl, FormField, FormItem, FormLabel } from "components/Form";
import Header from "components/Header";
import MainMenu from "components/MainMenu";
import Switch from "components/Switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";

import useCategories from "./useCategories";

export default function Categories() {
  const {
    isLoadingSession,
    isLoadingSettings,
    categories,
    form,
    handleSubmit,
    control,
    isDirty,
    isSubmitting,
    onSubmit,
    nameKey,
    descriptionKey,
    aiEnhancmentLevel,
    setAiEnhancementLevel,
    aiCategoriesEnabled,
    setAiCategoriesEnabled,
    atLeastOneCategorySelected,
  } = useCategories();

  const { t } = useTranslation();

  useIsActive();

  return (
    <>
      <Header className="justify-between">
        <div className="flex items-center gap-2">
          <Link to={`${ROUTES.PROFILE}?tab=findings`} className="flex items-center cursor-pointer">
            <ArrowLeftIcon />
          </Link>
          {t("profile.categories.pageTitle")}
        </div>
        <MainMenu />
      </Header>
      <Content isLoading={isLoadingSession || isLoadingSettings}>
        <Container className="pb-30">
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-6 justify-between px-4 md:px-0 pt-16 mb-20 md:mb-0"
              id="report-processing-form"
            >
              {/* Section 1 — AI Processing Level */}
              <div className="flex flex-col gap-3">
                <div>
                  <p className="text-sm font-semibold">{t("profile.categories.aiProcessingLevel")}</p>
                </div>
                <ButtonGroup className="w-full">
                  <Button
                    type="button"
                    className="flex-1"
                    variant={aiEnhancmentLevel === 0 ? "primary" : "secondary"}
                    onClick={() => setAiEnhancementLevel(0)}
                  >
                    {t("profile.categories.processingNone")}
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    variant={aiEnhancmentLevel === 1 ? "primary" : "secondary"}
                    onClick={() => setAiEnhancementLevel(1)}
                  >
                    {t("profile.categories.processingMinimal")}
                  </Button>
                </ButtonGroup>
                <p className="text-sm text-muted-foreground">
                  {aiEnhancmentLevel === 0
                    ? t("profile.categories.processingNoneDescription")
                    : t("profile.categories.processingMinimalDescription")}
                </p>
              </div>

              {/* Section 2 — Categorization */}
              <div className="flex flex-col gap-3">
                <div
                  className={`flex flex-row items-center justify-between rounded-md p-4 transition-colors ${
                    aiCategoriesEnabled ? "bg-general-green-600" : "bg-general-gray-300"
                  }`}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold">{t("profile.categories.categorization")}</span>
                    <span className="text-xs text-muted-foreground">
                      {t("profile.categories.categorizationSubtitle")}
                    </span>
                  </div>
                  <Switch checked={aiCategoriesEnabled} onCheckedChange={setAiCategoriesEnabled} />
                </div>

                {aiCategoriesEnabled && (
                  <div className="flex flex-col gap-2">
                    <div className="px-1">
                      <p className="text-sm font-semibold">{t("profile.categories.activeCategories")}</p>
                      <p className="text-xs text-muted-foreground">
                        {t("profile.categories.activeCategoriesSubtitle")}
                      </p>
                    </div>
                    {categories.map((cat) => (
                      <FormField
                        key={cat.id}
                        control={control}
                        name={cat.code}
                        render={({ field }) => (
                          <FormItem
                            className={`flex flex-row items-center justify-between rounded-md p-4 transition-colors ${
                              field.value ? "bg-general-green-600" : "bg-general-gray-300"
                            }`}
                          >
                            <FormLabel>
                              {cat[nameKey as keyof typeof cat]}{" "}
                              <Tooltip>
                                <TooltipTrigger>
                                  <InfoIcon />
                                </TooltipTrigger>
                                <TooltipContent className="w-80">
                                  {cat[descriptionKey as keyof typeof cat]}
                                </TooltipContent>
                              </Tooltip>
                            </FormLabel>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    ))}
                    {!atLeastOneCategorySelected && (
                      <p className="text-sm text-destructive px-1">
                        {t("profile.categories.atLeastOneCategoryRequired")}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <Footer className="md:justify-end">
                <Button
                  className="w-full md:w-60"
                  type="submit"
                  disabled={!isDirty || isSubmitting || !atLeastOneCategorySelected}
                >
                  {t("button.saveChanges")}
                </Button>
              </Footer>
            </form>
          </Form>
        </Container>
      </Content>
    </>
  );
}
